import { LocalDate } from "@js-joda/core";
import currency from "currency.js";
import h from "trader-hyperscript";
import { rpc } from "trader.ts/network";
import { dyn } from "trader.ts/ui";
import { Form, textBox } from "trader.ts/ui.forms";
import {
  periodid,
  price_adapted_to_lg_and_student,
} from "../../../sql/classy/types";
import { renderSaveButton } from "../../client/savebutton";
import { typeaheadFieldMulti } from "../../client/typeaheadfield";
import {
  non_periodic_payment_scheme,
  periodic_payment_scheme,
} from "../../domain/config";
import { mandatoryValidation } from "../../lib/validate";
import { renderFirstNameLastName } from "../../render";
import * as routes from "../../routes";
import { invoicelinesField } from "../invoicedetails/client";
import {
  expectedPaymentField,
  paymentSchemeField,
} from "../planning/enrollmentform";
import { match } from "ts-pattern";

export function render(opts: {
  period: { firstday: LocalDate; id: periodid };
  payment_schemes: (non_periodic_payment_scheme | periodic_payment_scheme)[];
}): HTMLElement | HTMLElement[] {
  const form = new Form({})
    .addField("description", [], () =>
      textBox({
        initialVal: "",
        label: "Beschrijving factuur",
        validations: [mandatoryValidation],
      }),
    )
    .addField("students", [], async () =>
      typeaheadFieldMulti(
        ({ searchstring }) =>
          rpc(routes.searchStudentSpec, undefined, { searchstring }),
        renderFirstNameLastName,
        {
          maxCandidatesToShow: 5,
          initial: [],
          label: "Studenten",
        },
      ),
    )
    .addField("invoicelines", [], () =>
      invoicelinesField({
        invoicelines: [
          {
            description: "",
            amount: currency(0) as price_adapted_to_lg_and_student,
            vat: 0,
            ordering: 0,
          },
        ],
      }),
    )
    .addField("payment_scheme", [], () =>
      paymentSchemeField(opts.payment_schemes),
    )
    .addField(
      "expected_payments",
      ["payment_scheme", "invoicelines"],
      ({ payment_scheme, invoicelines }) =>
        expectedPaymentField(
          payment_scheme,
          invoicelines,
          {
            firstlesson: LocalDate.now(),
            lastlesson: LocalDate.now(),
            period_firstday: opts.period.firstday,
            created: LocalDate.now(),
          },
          [],
        ),
    )
    .build();

  return (
    <div>
      <section>{form.render()}</section>
      <div style="text-align: right;">
        {dyn(form.s, (val) => {
          return renderSaveButton({
            formS: form.s,
            disabled: (values) =>
              values.students.length === 0
                ? "Je moet minstens 1 student invullen"
                : values.invoicelines.reduce(
                    (acc, il) => acc.add(il.amount),
                    currency(0),
                  ).intValue <= 0
                ? "Het bedrag van deze factuur is onjuist"
                : false,
            save: (values) =>
              rpc(routes.makeStudentChargeBatchSpec, undefined, {
                ...values,
                period: { id: opts.period.id },
                students: values.students.map((s) => ({
                  id: s.id,
                  firstname: s.firstname,
                  lastname: s.lastname,
                })),
              }).then((s) => {
                return match(s)
                  .with({ tag: "success" }, (r) => ({
                    tag: "success" as const,
                    text: (
                      <div>
                        <div>
                          {"Facturen aangemaakt en berichten in de wachtrij gezet"}
                        </div>
                        {r.invoiceids.map((inv) => (
                          <div>
                            <span>
                              {renderFirstNameLastName(inv.student)}:{" "}
                              <a
                                href={routes.invoicedetailsRoute.link({
                                  invoiceid: inv.invoiceid,
                                  edit: false,
                                })}
                              >
                                {"#" + inv.invoiceexternalid}
                              </a>
                            </span>
                          </div>
                        ))}
                      </div>
                    ),
                  }))
                  .exhaustive();
              }),
            text: {
              normal: "Maak %n facturen".replace(
                "%n",
                val.tag === "parsed"
                  ? val.parsed.students.length.toString()
                  : "0",
              ),
            },
            timeout: 1000 * 60 * 60 * 24,
            reload: false,
          });
        })}
      </div>
    </div>
  );
}
